import { useEffect, useState } from 'react';
import { SxProps } from 'src/components/muiTheme/utils';
import { Theme } from 'src/components/muiTheme/utils';

interface UseIframeDialogPositioningOptions {
  /**
   * Enable debug logging to console
   * @default false
   */
  debug?: boolean;
  /**
   * Weight applied to parent scroll offset (0-1)
   * @default 0.3
   */
  parentScrollWeight?: number;
  /**
   * Minimum padding top in rem
   * @default 2
   */
  minPaddingTop?: number;
  /**
   * Scroll offset to rem conversion factor
   * @default 100
   */
  scrollToRemFactor?: number;
}

interface UseIframeDialogPositioningReturn {
  /**
   * Whether the current window is in an iframe
   */
  inIframe: boolean;
  /**
   * Current iframe scroll offset
   */
  iframeScrollOffset: number;
  /**
   * Current parent scroll offset (0 if cross-origin blocked)
   */
  parentScrollOffset: number;
  /**
   * Total calculated scroll offset
   */
  totalScrollOffset: number;
  /**
   * Calculated padding top in rem
   */
  paddingTopRem: number;
  /**
   * MUI sx prop for dialog positioning
   */
  dialogSx: SxProps<Theme> | undefined;
}

/**
 * Custom hook for positioning dialogs within iframes based on scroll position.
 *
 * Tracks both iframe and parent window scroll positions and calculates
 * appropriate dialog positioning to keep dialogs visible when scrolled.
 *
 * @param options Configuration options
 * @returns Dialog positioning data and sx styles
 *
 * @example
 * ```tsx
 * const { dialogSx, inIframe, totalScrollOffset } = useIframeDialogPositioning({
 *   debug: true,
 *   parentScrollWeight: 0.3
 * });
 *
 * const [confirmDialogProps, confirm] = useConfirmDialogComponent({
 *   sx: dialogSx,
 * });
 * ```
 */
export const useIframeDialogPositioning = (
  options: UseIframeDialogPositioningOptions = {}
): UseIframeDialogPositioningReturn => {
  const {
    debug = false,
    parentScrollWeight = 0.3,
    minPaddingTop = 2,
    scrollToRemFactor = 100,
  } = options;

  const [parentScrollOffset, setParentScrollOffset] = useState(0);
  const [iframeScrollOffset, setIframeScrollOffset] = useState(0);

  // Auto-detect iframe mode
  const inIframe = typeof window !== 'undefined' && window.parent !== window;

  // Track parent scroll position
  useEffect(() => {
    if (!inIframe) return;

    const handleParentScroll = () => {
      try {
        const newParentScroll = window.parent?.scrollY || 0;
        setParentScrollOffset(newParentScroll);

        if (debug) {
          // eslint-disable-next-line no-console
          console.log(
            '🔍 Parent scroll:',
            newParentScroll,
            'Iframe scroll:',
            iframeScrollOffset
          );
        }
      } catch (error) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log(
            '❌ Cross-origin parent scroll access blocked (dev only)'
          );
        }
      }
    };

    try {
      window.parent?.addEventListener('scroll', handleParentScroll);
      if (debug) {
        // eslint-disable-next-line no-console
        console.log('✅ Parent scroll listener attached successfully');
      }

      return () => {
        try {
          window.parent?.removeEventListener('scroll', handleParentScroll);
          if (debug) {
            // eslint-disable-next-line no-console
            console.log('🧹 Parent scroll listener removed');
          }
        } catch (error) {
          if (debug) {
            // eslint-disable-next-line no-console
            console.log('❌ Cross-origin parent listener removal blocked');
          }
        }
      };
    } catch (error) {
      if (debug) {
        // eslint-disable-next-line no-console
        console.log(
          '❌ Cross-origin parent scroll listener blocked (dev only)'
        );
      }
    }
  }, [inIframe, iframeScrollOffset, debug]);

  // Track iframe scroll position
  useEffect(() => {
    if (!inIframe) return;

    const handleIframeScroll = () => {
      const newIframeScroll = window.scrollY || 0;
      setIframeScrollOffset(newIframeScroll);

      if (debug) {
        // eslint-disable-next-line no-console
        console.log(
          '📜 Iframe scroll:',
          newIframeScroll,
          'Parent scroll:',
          parentScrollOffset
        );
      }
    };

    window.addEventListener('scroll', handleIframeScroll);
    if (debug) {
      // eslint-disable-next-line no-console
      console.log('✅ Iframe scroll listener attached');
    }

    return () => {
      window.removeEventListener('scroll', handleIframeScroll);
      if (debug) {
        // eslint-disable-next-line no-console
        console.log('🧹 Iframe scroll listener removed');
      }
    };
  }, [inIframe, parentScrollOffset, debug]);

  // Calculate positioning
  const totalScrollOffset =
    iframeScrollOffset + parentScrollOffset * parentScrollWeight;
  const paddingTopRem = Math.max(
    minPaddingTop,
    totalScrollOffset / scrollToRemFactor
  );

  // Debug total calculation
  useEffect(() => {
    if (inIframe && debug) {
      // eslint-disable-next-line no-console
      console.log('🧮 Scroll calculation:', {
        iframe: iframeScrollOffset,
        parent: parentScrollOffset,
        total: totalScrollOffset,
        paddingTop: `${paddingTopRem}rem`,
      });
    }
  }, [
    inIframe,
    iframeScrollOffset,
    parentScrollOffset,
    totalScrollOffset,
    paddingTopRem,
    debug,
  ]);

  // Generate sx prop for dialogs
  const dialogSx: SxProps<Theme> | undefined = inIframe
    ? {
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: `${paddingTopRem}rem`,
        },
      }
    : undefined;

  return {
    inIframe,
    iframeScrollOffset,
    parentScrollOffset,
    totalScrollOffset,
    paddingTopRem,
    dialogSx,
  };
};
